import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Sparkles, Radio, Mail } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useMouseParallax, useTilt } from "@/hooks/useMouseParallax";

/**
 * CONTACT HERO — "Transmission Terminal"
 * CRT terminal layout with typewriter and live oscilloscope wave.
 */
const ContactHero = ({ onOpenAudit, onScrollToForm }) => {
  const { t, lang } = useLang();
  const { ref, pos } = useMouseParallax();
  const tiltRef = useTilt(6);
  const [typed, setTyped] = useState("");
  const linesRef = useRef([]);
  linesRef.current = lang === "bg"
    ? [
        "> ИНИЦИИРАНЕ НА ВРЪЗКА ...",
        "> КАНАЛ: PRIME-COLLECTIVE/42.6977N 23.3219E",
        "> СТАТУС: ОНЛАЙН",
        "> ЗДРАВЕЙТЕ, РАЗКАЖЕТЕ НИ ЗА БИЗНЕСА ВИ.",
      ]
    : [
        "> ESTABLISHING CONNECTION ...",
        "> CHANNEL: PRIME-COLLECTIVE/42.6977N 23.3219E",
        "> STATUS: ONLINE",
        "> HELLO, HUMAN. TELL US ABOUT YOUR BUSINESS.",
      ];

  // Typewriter effect
  useEffect(() => {
    setTyped("");
    const lines = linesRef.current;
    let timer;
    let lineIdx = 0;
    let charIdx = 0;
    let out = "";
    const step = () => {
      if (lineIdx >= lines.length) return;
      const line = lines[lineIdx];
      if (charIdx <= line.length) {
        out = lines.slice(0, lineIdx).join("\n") + (lineIdx ? "\n" : "") + line.slice(0, charIdx);
        setTyped(out);
        charIdx += 1;
        timer = setTimeout(step, 28);
      } else {
        lineIdx += 1;
        charIdx = 0;
        timer = setTimeout(step, 280);
      }
    };
    step();
    return () => clearTimeout(timer);
  }, [lang]);

  return (
    <section
      ref={ref}
      data-testid="contact-hero"
      className="relative border-b border-brand-border overflow-hidden min-h-[88vh] flex items-center"
    >
      {/* Background */}
      <div className="absolute inset-0 crt-lines opacity-60 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="orb orb-teal w-[500px] h-[500px] -top-24 right-10 opacity-25 animate-float-slow"
          style={{ transform: `translate3d(${pos.x * -30}px, ${pos.y * -30}px, 0)` }}
        />
        <div
          className="orb orb-purple w-[480px] h-[480px] -bottom-20 -left-20 opacity-25 animate-float-slower"
          style={{ transform: `translate3d(${pos.x * 40}px, ${pos.y * 40}px, 0)` }}
        />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 pt-5 md:py-28 md:pt-7 w-full grid lg:grid-cols-12 gap-10 items-center">
        {/* Left column — headline */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-teal/40 bg-brand-teal/10 backdrop-blur-sm mb-8">
            <Radio className="w-3.5 h-3.5 text-brand-tealLight animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-tealLight">
              {lang === "bg" ? "// приемане на сигнал" : "// incoming signal"}
            </span>
          </div>

          <p className="text-xs font-mono uppercase tracking-[0.35em] text-brand-purpleLight mb-6 ml-1">{t.contact.tag}</p>

          <h1 className="font-heading text-[3rem] md:text-[5.2rem] lg:text-[6.2rem] font-black uppercase leading-[0.86] tracking-tighter">
            <span className="block">{lang === "bg" ? "Нека" : "Let's"}</span>
            <span className="block chrome-text">{lang === "bg" ? "установим" : "establish"}</span>
            <span className="block holo-text glitch" data-text={lang === "bg" ? "връзка." : "contact."}>
              {lang === "bg" ? "връзка." : "contact."}
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-zinc-400 leading-relaxed text-base md:text-lg">{t.contact.sub}</p>

          {/* Coords pill that follows mouse */}
          <div className="mt-6 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            <span className="text-brand-pinkLight">LAT</span>
            <span>{(42.6977 + pos.y * 0.4).toFixed(4)}°N</span>
            <span className="text-brand-pinkLight">LNG</span>
            <span>{(23.3219 + pos.x * 0.4).toFixed(4)}°E</span>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              data-testid="contact-hero-form"
              onClick={onScrollToForm}
              className="inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-7 py-5 rounded-full text-xs"
            >
              <Mail className="w-4 h-4" />
              {lang === "bg" ? "Изпрати запитване" : "Send inquiry"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              data-testid="contact-hero-audit"
              onClick={onOpenAudit}
              className="inline-flex items-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-7 py-5 rounded-full text-xs"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "bg" ? "Безплатен audit" : "Free audit"}
            </button>
          </div>
        </div>

        {/* Right column — terminal */}
        <div className="lg:col-span-5">
          <div
            ref={tiltRef}
            className="relative rounded-2xl gradient-border bg-brand-bg/85 backdrop-blur-xl overflow-hidden shadow-[0_0_60px_-10px_rgba(16,185,129,0.35)] transition-transform duration-200 will-change-transform"
          >
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-brand-border bg-brand-surface/70">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-pink/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-brand-purpleLight/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-brand-tealLight/90" />
              <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                prime.terminal ~ v26
              </span>
            </div>

            {/* Terminal body */}
            <div className="p-5 md:p-6 font-mono text-sm leading-relaxed min-h-[230px]">
              <pre className="whitespace-pre-wrap break-words text-brand-tealLight caret">{typed}</pre>
            </div>

            {/* Oscilloscope */}
            <div className="px-5 md:px-6 pb-5 md:pb-6">
              <svg viewBox="0 0 400 60" className="w-full h-14 text-brand-tealLight">
                <defs>
                  <linearGradient id="wave-grad" x1="0" x2="1">
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="50%" stopColor="#34D399" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,30 Q25,5 50,30 T100,30 T150,30 T200,30 T250,30 T300,30 T350,30 T400,30"
                  fill="none"
                  stroke="url(#wave-grad)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  style={{
                    filter: "drop-shadow(0 0 6px rgba(52,211,153,0.5))",
                    animation: "holo-rotate 0s", // no-op, keep transform intact
                  }}
                >
                  <animate
                    attributeName="d"
                    dur="4s"
                    repeatCount="indefinite"
                    values="
                      M0,30 Q25,5 50,30 T100,30 T150,30 T200,30 T250,30 T300,30 T350,30 T400,30;
                      M0,30 Q25,55 50,30 T100,30 T150,30 T200,30 T250,30 T300,30 T350,30 T400,30;
                      M0,30 Q25,5 50,30 T100,30 T150,30 T200,30 T250,30 T300,30 T350,30 T400,30
                    "
                  />
                </path>
              </svg>
              <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <span>{lang === "bg" ? "SIGNAL · STABLE" : "SIGNAL · STABLE"}</span>
                <span>42.6977°N · 23.3219°E</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactHero;
